/** @jsxImportSource ./simboluri-jsx */
import TextSimboluri, { S } from "./Simbol";
import { t, useT, useTx } from "../i18n";
import type { Cheie } from "../i18n";
import Marime from "./Marime";

// CARDUL MODELELOR DE RIGIDITATE — singurul loc din interfață care spune ce
// model a produs ce cifră (structurist; v. structure/rigiditate.py).
//
// Trei modele, trei întrebări, pe aceeași geometrie:
//   M1 «fundație»  beton întreg            → reacțiuni + infrastructură
//   M3 «eforturi»  0,8/0,8/0,5/h_placă      → M/V/N pe suprastructură
//   M2 «drift»     0,5 uniform              → d_re, c, θ, α
//   M4 «eforturi, baza încastrată» — numai când ΔT₁ > prag
//   M1·fix «cutia rigidă» — beton întreg cu baza încastrată, doar drift (I2m)
//
// Fiecare pas care rulează sau citește mai multe modele afișează cardul cu
// datele LUI (Pasul 7: eigen-urile; 8: staticele; 9: RSA; 10: sursele; 11/12:
// proveniența tensorului combinat). Regula: două numere de pe același ecran
// par comparabile — dacă vin de pe modele diferite, trebuie să scrie asta.

const ETICHETE: Record<string, { scurt: string; nume: Cheie; ce: Cheie }> = {
  fundatie: {
    scurt: "M1", nume: "struct.mod.fundatie_nume", ce: "struct.mod.fundatie_ce",
  },
  eforturi: {
    scurt: "M3", nume: "struct.mod.eforturi_nume", ce: "struct.mod.eforturi_ce",
  },
  drift: {
    scurt: "M2", nume: "struct.mod.drift_nume", ce: "struct.mod.drift_ce",
  },
  eforturi_incastrat: {
    scurt: "M4", nume: "struct.mod.incastrat_nume", ce: "struct.mod.incastrat_ce",
  },
  cutie_incastrat: {
    scurt: "M1·fix", nume: "struct.mod.cutie_nume", ce: "struct.mod.cutie_ce",
  },
};

const ORDINE = ["fundatie", "eforturi", "drift", "eforturi_incastrat", "cutie_incastrat"];

function nr(v: any, d = 3): string {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return Number(v).toFixed(d);
}

function T1(per: any): string {
  if (!per) return "—";
  // Pasul 7 dă {X: {T_s, mod}}, Pasul 9 dă {X: T_s}
  const x = typeof per.X === "object" ? per.X?.T_s : per.X;
  const y = typeof per.Y === "object" ? per.Y?.T_s : per.Y;
  return t("struct.mod.xy_s", { x: nr(x), y: nr(y) });
}

function Masuratori({ m }: { m: any }) {
  const t = useT();
  if (!m) return null;
  return (
    <span className="sg-muted">
      {m.secunde != null && t("struct.mod.secunde", { n: m.secunde })}
      {m.rss_mb != null && t("struct.mod.mb", { n: Math.round(m.rss_mb) })}
      {m.in_proces === false && t("struct.mod.proces_separat")}
      {m.in_proces === true && t("struct.mod.in_proces")}
    </span>
  );
}

// cele trei propoziții ale deciziei «încastrat vs elastic» (
// „doar să fie clar exprimat în interfață") — aceleași pe orice pas
function Decizia({ info }: { info?: any }) {
  const t = useT();
  const tx = useTx();
  const activ = !!info?.activ;
  return (
    <div className="sg-muted" style={{ marginTop: 8, fontSize: 12, lineHeight: 1.5 }}>
      <strong>{t("struct.mod.al_patrulea")}</strong>
      {tx("struct.mod.decizie")}
      {info?.prag_delta_t1_pct != null
        ? t("struct.mod.prag", { n: info.prag_delta_t1_pct }) : ""}
      {tx("struct.mod.decizie2")}
      {info && (
        <div style={{ marginTop: 4 }}>
          {t("struct.mod.pe_proiect")}
          <strong>{t(activ ? "struct.mod.m4_activ" : "struct.mod.fara_m4")}</strong>
          {info.delta_T1_pct != null && t("struct.mod.delta", { n: info.delta_T1_pct })}
          {info.motiv && <> · <TextSimboluri text={String(info.motiv)} /></>}
        </div>
      )}
    </div>
  );
}

function Randuri({ modele, ordine, coloane, celule }: {
  modele: Record<string, any>;
  ordine: string[];
  coloane: string[];
  celule: (id: string, m: any) => any[];
}) {
  const t = useT();
  const ids = (ordine.length ? ordine : ORDINE).filter((id) => modele[id]);
  if (!ids.length) return null;
  return (
    <table className="sg-table" style={{ marginTop: 6 }}>
      <thead>
        <tr>
          <th>{t("struct.mod.col_model")}</th>
          <th>{t("struct.mod.col_rigiditate")}</th>
          {coloane.map((c) => <th key={c}>{c}</th>)}
        </tr>
      </thead>
      <tbody>
        {ids.map((id) => {
          const e = ETICHETE[id];
          const m = modele[id] ?? {};
          return (
            <tr key={id}>
              <td><strong>{e ? e.scurt : id}</strong>{e ? ` — ${t(e.nume)}` : ""}</td>
              <td style={{ fontSize: 12, maxWidth: 360 }}>
                <TextSimboluri text={e ? t(e.ce) : ""} />
                {id === "drift" && m.baza && (
                  <div style={{ marginTop: 4 }}>
                    <strong>{t(m.baza === "incastrat" ? "struct.mod.baza_incastrata" : "struct.mod.baza_elastica")}</strong>
                    {m.baza_decizie?.motiv && <> — <TextSimboluri text={String(m.baza_decizie.motiv)} /></>}
                  </div>
                )}
                {m.eroare && (
                  <div className="st-bad">
                    {t("struct.mod.eroare", { eroare: String(m.eroare).slice(0, 200) })}
                  </div>
                )}
              </td>
              {celule(id, m).map((v, i) => <td key={i} className="sg-mono">{v}</td>)}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default function ModeleRigiditate({ pas, r }: { pas: 7 | 8 | 9 | 10 | 11 | 12; r: any }) {
  const t = useT();
  const tx = useTx();
  if (!r) return null;
  let corp: any = null;
  let sub: any = null;

  if (pas === 7) {
    const me = r.modele_eigen ?? {};
    const vm = r.verificare_modala ?? {};
    const moduri: any[] = vm.moduri ?? [];
    const t1 = (cheie: string) => {
      if (!moduri.length) return null;
      const m = moduri.reduce((a, b) => ((b[cheie] ?? 0) > (a[cheie] ?? 0) ? b : a));
      return { T_s: m.T_s, mod: m.mod };
    };
    const modele: Record<string, any> = {
      fundatie: { T1_s: { X: t1("masa_x_pct"), Y: t1("masa_y_pct") },
                  nr_moduri: vm.nr_moduri, criteriu_90_ok: vm.criteriu_90_ok,
                  masuratori: { in_proces: true } },
      ...me,
    };
    if (!Object.keys(me).length) return null;   // rezultat de dinaintea celor trei modele
    corp = (
      <Randuri modele={modele} ordine={["fundatie", "eforturi", "drift"]}
        coloane={[t("struct.mod.col_t1_dir"), t("struct.mod.col_moduri"),
                  t("struct.mod.col_masa90"), t("struct.mod.col_eigen")]}
        celule={(_id, m) => [T1(m.T1_s), m.nr_moduri ?? "—",
          m.criteriu_90_ok == null ? "—" : m.criteriu_90_ok ? "✓" : "✗",
          <Masuratori m={m.masuratori} />]} />
    );
    sub = (
      <>{tx("struct.mod.sub7", {
        paralelism: me.drift?.paralelism ?? me.eforturi?.paralelism ?? "—",
      })}</>
    );
  } else if (pas === 8) {
    const modele = r.modele ?? {};
    if (!Object.keys(modele).length) return null;
    corp = (
      <Randuri modele={modele} ordine={r.modele_ordine ?? []}
        coloane={[t("struct.mod.col_tensor"), t("struct.mod.col_echilibru"),
                  t("struct.mod.col_sarite"), t("struct.mod.col_statice")]}
        celule={(_id, m) => [m.tensor ?? "—",
          m.echilibru_ok == null ? "—" : m.echilibru_ok ? "✓" : "✗",
          (m.cazuri_sarite ?? []).join(", ") || "—",
          <Masuratori m={m.masuratori} />]} />
    );
    sub = <>{t("struct.mod.sub8")}</>;
  } else if (pas === 9) {
    const modele = r.modele ?? {};
    if (!Object.keys(modele).length) return null;
    corp = (
      <Randuri modele={modele} ordine={r.modele_ordine ?? []}
        coloane={["T₁", t("struct.mod.col_fb"), t("struct.mod.col_vbaza"),
                  t("struct.mod.col_moduri"), t("struct.mod.col_eigen"),
                  t("struct.mod.col_rsa")]}
        celule={(_id, m) => [T1(m.T1_s),
          m.f_b_kn
            ? t("struct.mod.xy_kn", { x: nr(m.f_b_kn.X, 0), y: nr(m.f_b_kn.Y, 0) })
            : "—",
          m.v_baza_kn && (m.v_baza_kn.X || m.v_baza_kn.Y)
            ? t("struct.mod.xy_kn", { x: nr(m.v_baza_kn.X, 0), y: nr(m.v_baza_kn.Y, 0) })
            : "—",
          m.nr_moduri ?? "—",
          m.metoda ? t("struct.mod.impingere")
            : m.criteriu_masa_modala?.sursa_moduri?.includes("nerecalculat")
              ? t("struct.mod.din_cache") : t("struct.mod.recalculat"),
          <Masuratori m={m.masuratori} />]} />
    );
    sub = (
      <>{tx("struct.mod.sub9", { model: r.model_suprastructurii ?? "eforturi" })}</>
    );
  } else if (pas === 10) {
    const s = r.surse;
    if (!s) return null;
    corp = (
      <table className="sg-table" style={{ marginTop: 6 }}>
        <tbody>
          <tr><td><S k="d_re">d_re</S></td><td><TextSimboluri text={s.d_re ?? "—"} /></td></tr>
          <tr><td>{t("struct.mod.factorul_c")}</td><td><TextSimboluri text={s.c ?? "—"} /></td></tr>
          <tr><td><S k="theta">θ</S>, α</td><td><TextSimboluri text={s.theta ?? "—"} /></td></tr>
          <tr><td>{t("struct.mod.i2m")}</td><td><TextSimboluri text={s.I2m ?? "—"} /></td></tr>
          <tr><td>{t("struct.mod.bici")}</td><td><TextSimboluri text={s.efect_bici ?? "—"} /></td></tr>
        </tbody>
      </table>
    );
    sub = <>{t("struct.mod.sub10")}</>;
  } else {
    const p = r.provenienta;
    if (!p) return null;
    const e = (id?: string) =>
      id && ETICHETE[id] ? `${ETICHETE[id].scurt} «${id}»` : id ?? "—";
    corp = (
      <table className="sg-table" style={{ marginTop: 6 }}>
        <tbody>
          <tr>
            <td>{t("struct.mod.suprastructura")}</td>
            <td><strong>{e(p.suprastructura?.model)}</strong>
              {p.suprastructura?.nr_elemente != null &&
                t("struct.mod.nr_elemente", { n: p.suprastructura.nr_elemente })}
              {p.suprastructura?.nr_taieturi_placa != null &&
                t("struct.mod.nr_taieturi", { n: p.suprastructura.nr_taieturi_placa })}</td>
          </tr>
          <tr>
            <td>{t("struct.mod.infrastructura")}</td>
            <td><strong>{e(p.infrastructura?.model)}</strong>
              {p.infrastructura?.nr_elemente != null &&
                t("struct.mod.nr_elemente", { n: p.infrastructura.nr_elemente })}
              {p.infrastructura?.nr_panouri != null &&
                t("struct.mod.nr_panouri", { n: p.infrastructura.nr_panouri })}
              {p.infrastructura?.nr_taieturi_placa != null &&
                t("struct.mod.nr_taieturi", { n: p.infrastructura.nr_taieturi_placa })}</td>
          </tr>
          <tr><td>{t("struct.mod.reactiunile")}</td><td><strong>{e(p.reactiuni?.model)}</strong></td></tr>
          {p.alpha && <tr><td>{t("struct.mod.alpha")}</td><td><strong>{e(p.alpha.model)}</strong></td></tr>}
        </tbody>
      </table>
    );
    sub = (
      <>
        {p.fallback && (
          <div className="st-bad" style={{ marginBottom: 4 }}>
            {t("struct.mod.cadere", { motiv: p.fallback })}
          </div>
        )}
        <TextSimboluri text={p.nota ?? ""} />
      </>
    );
  }

  return (
    <div className="st-findings" style={{ border: "1px solid #ecd6a8", borderRadius: 6, padding: "8px 12px", marginBottom: 10 }}>
      <strong>{t("struct.mod.titlu")}</strong>
      <Marime cheie="modele_rigiditate" />
      {t("struct.mod.subtitlu")}
      {corp}
      {sub && <div className="sg-muted" style={{ marginTop: 6, fontSize: 12, maxWidth: 1000 }}>{sub}</div>}
      <Decizia info={r.doua_modele ?? (pas === 7 ? r.fundatie?.impact : undefined)} />
    </div>
  );
}
